import {FormikProps, getIn} from 'formik'
import {cloneDeep, isEqual} from 'lodash'
import {FC, useMemo} from 'react'
import Select from 'react-select'
import {getCSSVariableValue} from '../../../assets/ts/_utils'
import {
  DS_SEPARATOR,
  DS_TEAL,
  DS_TEAL_LIGHT,
  DS_TEXT_DARK,
  DS_TEXT_MUTED,
  INVALID_COLOR,
  VALID_COLOR,
} from '../../../constants'
import {toAbsoluteUrl} from '../../../helpers'

type Props = {
  onChangeCallback?: any
  className?: string
  labelClassName?: string
  classNameSelect?: string
  label?: string
  options: Array<any>
  required?: boolean
  formik: FormikProps<any>
  name: string
  placeholder?: string
  emptyDefault?: boolean
  disabled?: boolean
  onFocus?: any
  checkFormik?: boolean
}

const SelectMultiLevelFormik: FC<Props> = ({
  className = '',
  labelClassName = '',
  classNameSelect = '',
  label = null,
  options,
  required = false,
  formik,
  name,
  placeholder = '',
  emptyDefault = true,
  disabled = false,
  onFocus,
  checkFormik = true,
  onChangeCallback,
}) => {
  let fieldProps = formik.getFieldProps(name)
  let formikErrors = getIn(formik.errors, name)
  let formikTouched = getIn(formik.touched, name)

  let isInvalid = formikTouched && formikErrors && checkFormik
  let isValid = formikTouched && !formikErrors && fieldProps.value && checkFormik
  const customStyles = {
    menuPortal: (base) => ({...base, zIndex: 9999}),
    option: (styles, {isSelected, isDisabled}) => {
      return {
        ...styles,
        minHeight: 30,
        backgroundColor: isSelected && DS_TEAL,
        '&:hover': {
          background: !isSelected && DS_TEAL_LIGHT,
        },
        cursor: isDisabled ? 'not-allowed' : 'pointer',
      }
    },
    singleValue: (base) => ({
      ...base,
      color: getCSSVariableValue('$gray-700'),
    }),
    control: (base, {menuIsOpen}) => ({
      ...base,
      background: (disabled && '#eff2f5') || '#ffffff',
      backgroundImage: isValid
        ? `url(${toAbsoluteUrl('/media/gori/valid.svg')})`
        : isInvalid
        ? `url(${toAbsoluteUrl('/media/gori/invalid.svg')})`
        : '',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 3.5rem center',
      backgroundSize: 'calc(0.75em + 0.75rem) calc(0.75em + 0.75rem)',
      borderColor: menuIsOpen
        ? DS_TEAL
        : isValid
        ? VALID_COLOR
        : isInvalid
        ? INVALID_COLOR
        : DS_SEPARATOR,
      '&:hover': {
        borderColor: menuIsOpen
          ? DS_TEAL
          : isValid
          ? VALID_COLOR
          : isInvalid
          ? INVALID_COLOR
          : DS_SEPARATOR,
        background: disabled ? '#eff2f5' : DS_TEAL_LIGHT,
      },
      boxShadow: 'none',
      fontSize: '14px',
      fontWeight: '500',
      color: menuIsOpen ? DS_TEXT_DARK : DS_TEXT_MUTED,
      lineHeight: '1.5',
      letterSpacing: '-0.308px',
      padding: '3px',
      borderRadius: '8px',
      cursor: disabled ? 'default' : 'pointer',
    }),
    dropdownIndicator: (base, {selectProps}) => ({
      ...base,
      color: selectProps.menuIsOpen ? DS_TEXT_DARK : DS_TEXT_MUTED,
      '&:hover': {color: DS_TEAL},
    }),
    indicatorSeparator: (base) => ({
      ...base,
      backgroundColor: DS_SEPARATOR,
      '&:hover': {backgroundColor: DS_TEAL},
    }),
  }

  const value = useMemo(() => {
    const optionsValue: any = []
    cloneDeep(options).forEach((item: any) => optionsValue.push(...item.options))

    const option = cloneDeep(optionsValue).filter((item) => {
      if (isEqual(item.value, fieldProps.value)) {
        return item
      } else {
        return undefined
      }
    })
    return option[0]
  }, [fieldProps.value, options])

  return (
    <>
      {label && (
        <label className={`form-label ${labelClassName} ${required ? 'required' : ''}`}>
          {label}
        </label>
      )}
      <div className={className}>
        <Select
          isDisabled={disabled}
          className={classNameSelect}
          menuPortalTarget={document.body}
          menuPosition={'fixed'}
          styles={customStyles}
          theme={(theme) => ({
            ...theme,
            borderRadius: 8,
          })}
          options={emptyDefault ? [{label: '', value: ''}, ...options] : options}
          placeholder={placeholder}
          onFocus={onFocus}
          {...fieldProps}
          value={value ? value : ''}
          onChange={(option: any) => {
            formik.setFieldValue(fieldProps.name, option.value)
            onChangeCallback?.(option)
          }}
        />
        {formikTouched && formikErrors && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block text-danger'>
              <span role='alert'>{formikErrors}</span>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export {SelectMultiLevelFormik}
